import { BarChart3, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatCurrency, formatDate, formatPercent } from '@/lib/utils';
import type { PortfolioSummary } from '@/types';

interface PortfolioCardProps {
  summary: PortfolioSummary | null;
  isLoading?: boolean;
  className?: string;
}

function StatItem({
  label,
  value,
  icon: Icon,
  valueClassName,
}: {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-muted-foreground">
        {Icon && <Icon className="h-4 w-4" />}
        <span className="text-sm">{label}</span>
      </div>
      <span className={cn('font-medium', valueClassName)}>{value}</span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex justify-between">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function PortfolioCard({ summary, isLoading, className }: PortfolioCardProps) {
  const totalPnl = summary?.total_pnl ?? 0;
  const dayPnl = summary?.day_pnl ?? 0;
  const isProfitable = totalPnl >= 0;
  const isDayProfitable = dayPnl >= 0;

  const TrendIcon = isProfitable ? TrendingUp : TrendingDown;

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium flex items-center justify-between">
          <span>Portfolio Summary</span>
          <TrendIcon className={cn('h-5 w-5', isProfitable ? 'text-green-600' : 'text-red-600')} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton />
        ) : summary ? (
          <div className="space-y-4">
            {/* Total Value - Robinhood-style prominent display */}
            <div>
              <p className="text-sm text-muted-foreground">Total Value</p>
              <p
                className={cn(
                  'text-4xl font-bold tracking-tight',
                  isProfitable ? 'text-green-600' : 'text-red-600'
                )}
              >
                {formatCurrency(summary.total_value)}
              </p>
              <p
                className={cn(
                  'text-sm font-medium mt-1',
                  isProfitable ? 'text-green-600' : 'text-red-600'
                )}
              >
                {isProfitable ? '+' : ''}
                {formatCurrency(totalPnl)} ({formatPercent(summary.total_pnl_percent)}) all time
              </p>
            </div>

            {/* Stats */}
            <div className="space-y-3">
              <StatItem
                label="Cash Balance"
                value={formatCurrency(summary.cash_balance)}
                icon={Wallet}
              />
              <StatItem
                label="Positions Value"
                value={formatCurrency(summary.positions_value)}
                icon={BarChart3}
              />
              <StatItem
                label="Total P&L"
                value={`${formatCurrency(totalPnl)} (${formatPercent(summary.total_pnl_percent)})`}
                valueClassName={isProfitable ? 'text-green-600' : 'text-red-600'}
              />
              <StatItem
                label="Day P&L"
                value={`${formatCurrency(dayPnl)} (${formatPercent(summary.day_pnl_percent)})`}
                valueClassName={isDayProfitable ? 'text-green-600' : 'text-red-600'}
              />
            </div>

            {/* Last Updated */}
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                Last updated: {formatDate(summary.updated_at)}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">No portfolio data available</p>
        )}
      </CardContent>
    </Card>
  );
}
